/**
 * DemoModeContext — Wave 8 two-tier gate + realtime revocation.
 *
 * Two independent axes control whether the studio is showing the
 * in-memory Preview Studio overlay:
 *
 *   1. `canUseDemo` — admin-granted. Read from
 *      `profiles.demo_enabled` on the current user's row. Only
 *      Orbit admins can flip it (see supabase migration
 *      20260720600000_wave8_demo_access.sql). Realtime-subscribed
 *      so an admin revocation disables Preview Studio within a few
 *      seconds without a refresh.
 *
 *   2. `isDemo` — session-local. The user's own toggle in
 *      Settings → Developer. Persisted to localStorage but ignored
 *      when `canUseDemo` is false, so revocation is instant.
 *
 * Studio-facing UI never says the word "demo" — the toggle card
 * reads "Preview Studio", any active-state chip reads "Preview
 * active", etc. See CLAUDE.md Wave 8 keynote requirement.
 *
 * Backwards compatibility: every existing hook that destructures
 * `{ isDemo }` from `useDemoMode()` keeps working unchanged. The
 * new fields are additive.
 *
 * `isDeveloperMode` + `verifyDeveloperPassword` are retained for
 * the existing password-gated developer console (test-checkout
 * buttons, onboarding replay). Preview Studio no longer needs the
 * password step — admin-granted access is the security boundary.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { syncStorage } from '@/lib/storage';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface DemoModeContextType {
  /** Admin-granted access to Preview Studio. False for everyone by default. */
  canUseDemo: boolean;
  /** True while the current session is running against the demo overlay. */
  isDemo: boolean;
  /** No-op when canUseDemo is false. */
  setDemo: (value: boolean) => void;
  /** Kept for backwards compatibility — legacy consumers use this name. */
  setIsDemo: (value: boolean) => void;
  toggleDemo: () => void;
  /** Developer console gate (test-checkout, onboarding replay). Independent of Preview Studio. */
  isDeveloperMode: boolean;
  setIsDeveloperMode: (value: boolean) => void;
  verifyDeveloperPassword: (password: string) => boolean;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

const DEMO_MODE_KEY = 'orbit_demo_mode';
const DEVELOPER_MODE_KEY = 'orbit_developer_mode';
// Kept intentionally low-friction — this gate is a safety net against
// clicking through to the developer console, not a real security
// boundary. Preview Studio is now gated by profiles.demo_enabled +
// admin-only RLS; the password below only unlocks the legacy
// developer tooling (test-checkout, onboarding replay).
const DEVELOPER_PASSWORD = 'demo';

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [canUseDemo, setCanUseDemo] = useState(false);

  // Wave 8 spec: Preview Studio is SESSION-SCOPED. Refresh = clean
  // slate. Prior versions read this from localStorage on init, which
  // meant any user who ever flipped it on (or was migrated from the
  // pre-Wave-8 `orbit_demo_mode` flag) kept the "on" state across
  // refreshes — which is why the Galaxy / Passport surface was
  // rendering seeded demo planets even when the studio hadn't toggled
  // Preview Studio in the current session. Init to false, and wipe
  // any stale key from previous builds on mount below.
  const [isDemo, setIsDemoState] = useState(false);

  const [isDeveloperMode, setIsDeveloperModeState] = useState(() => {
    const stored = syncStorage.get(DEVELOPER_MODE_KEY);
    return stored !== null ? stored === 'true' : false;
  });

  // ─── canUseDemo: fetch + realtime subscription ───────────────────
  // We read profiles.demo_enabled for the current user on mount and
  // subscribe to that specific row. If an admin revokes access
  // mid-session the flag flips false and the effect below force-
  // exits Preview Studio, so any subsequent read/write goes back to
  // the real Supabase path.
  useEffect(() => {
    if (!user?.id) {
      setCanUseDemo(false);
      return;
    }

    let cancelled = false;

    const refresh = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('demo_enabled')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setCanUseDemo(
        Boolean(
          (data as { demo_enabled?: boolean | null } | null)?.demo_enabled,
        ),
      );
    };

    void refresh();

    // Realtime: any UPDATE to this user's profile row re-checks the
    // flag. Cheap — we only subscribe to a single row filter.
    const channel = supabase
      .channel(`profile-demo-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          const next = Boolean(
            (payload.new as { demo_enabled?: boolean | null } | null)?.demo_enabled,
          );
          setCanUseDemo(next);
        },
      )
      .subscribe();

    // Belt + suspenders: also re-fetch on window focus and every 5
    // seconds. Realtime is supposed to cover this, but during
    // testing it kept silently dropping updates (admin flips the
    // toggle → nothing propagates to the target session). These
    // fallbacks guarantee that if an admin changes the flag, the
    // user's session picks it up within 5s worst case, or instantly
    // if they tab back to the app after making the admin change.
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    const pollHandle = window.setInterval(() => {
      void refresh();
    }, 5_000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(pollHandle);
      void supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // If admin access is revoked while Preview Studio is on, exit it
  // immediately. Guarantees no in-memory writes get orphaned in a
  // stale-overlay state.
  useEffect(() => {
    if (!canUseDemo && isDemo) {
      setIsDemoState(false);
    }
  }, [canUseDemo, isDemo]);

  // Wave 8 cleanup: wipe the legacy `orbit_demo_mode` key once on
  // mount so any user who had it stuck to 'true' from a pre-Wave-8
  // build (or the earlier Wave-8 build that persisted this) starts
  // clean. We DO NOT write to it going forward — Preview Studio is
  // session-scoped in memory only.
  useEffect(() => {
    syncStorage.remove(DEMO_MODE_KEY);
  }, []);

  useEffect(() => {
    syncStorage.set(DEVELOPER_MODE_KEY, String(isDeveloperMode));
  }, [isDeveloperMode]);

  const setDemo = useCallback(
    (value: boolean) => {
      // Preview Studio only available when admin has granted access.
      if (value && !canUseDemo) return;
      setIsDemoState(value);
    },
    [canUseDemo],
  );

  const toggleDemo = useCallback(() => {
    if (!canUseDemo) return;
    setIsDemoState((prev) => !prev);
  }, [canUseDemo]);

  const setIsDeveloperMode = useCallback((value: boolean) => {
    setIsDeveloperModeState(value);
  }, []);

  const verifyDeveloperPassword = useCallback((password: string): boolean => {
    return password === DEVELOPER_PASSWORD;
  }, []);

  const value = useMemo<DemoModeContextType>(
    () => ({
      canUseDemo,
      isDemo,
      setDemo,
      // Legacy alias kept so existing callers (e.g. legacy checkbox
      // handlers wired to setIsDemo directly) don't break.
      setIsDemo: setDemo,
      toggleDemo,
      isDeveloperMode,
      setIsDeveloperMode,
      verifyDeveloperPassword,
    }),
    [
      canUseDemo,
      isDemo,
      setDemo,
      toggleDemo,
      isDeveloperMode,
      setIsDeveloperMode,
      verifyDeveloperPassword,
    ],
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    // Safe fallback (e.g. during Vite HMR when this module is re-evaluated
    // but the provider tree above hasn't remounted yet). Returning a no-op
    // default keeps the app rendering instead of blank-screening.
    return {
      canUseDemo: false,
      isDemo: false,
      setDemo: () => {},
      setIsDemo: () => {},
      toggleDemo: () => {},
      isDeveloperMode: false,
      setIsDeveloperMode: () => {},
      verifyDeveloperPassword: () => false,
    } satisfies DemoModeContextType;
  }
  return context;
}
